// js/router.js
// Client-side History API router for the SPA.
// Routes are matched top-to-bottom (most specific first).
// Each route's `viewLoader` is a lazy import so JS is only loaded when needed.

import { appState } from "./state.js";
import {
  PLATFORM_DOMAIN,
  getShopSlugFromSubdomain,
  shopCustomDomain,
  getActiveSubdomainSlug,
} from "./marketing/config.js";

// ─── Route definitions ────────────────────────────────────────────────────────
// guard: 'public'   → redirects logged-in users away
//        'customer' → requires auth + role === 'customer'
//        'owner'    → requires auth + role === 'owner'

const ROUTES = [
  // Shop customer landing page (public — accessible to everyone, logged in or not)
  {
    pattern: "/s/:slug",
    viewLoader: () => import("./views/customer/shopLandingView.js"),
    guard: "any",
  },
  // Owner legacy / form routes — redirect to /owner/shop which now handles these in modals
  {
    pattern: "/owner/rewards/new",
    viewLoader: () =>
      Promise.resolve({
        mount: (_, { navigate }) =>
          navigate("/owner/shop?tab=rewards", { replace: true }),
      }),
    guard: "owner",
  },
  {
    pattern: "/owner/rewards/:id/edit",
    viewLoader: () =>
      Promise.resolve({
        mount: (_, { navigate }) =>
          navigate("/owner/shop?tab=rewards", { replace: true }),
      }),
    guard: "owner",
  },
  {
    pattern: "/owner/services/new",
    viewLoader: () =>
      Promise.resolve({
        mount: (_, { navigate }) =>
          navigate("/owner/shop?tab=services", { replace: true }),
      }),
    guard: "owner",
  },
  {
    pattern: "/owner/services/:id/edit",
    viewLoader: () =>
      Promise.resolve({
        mount: (_, { navigate }) =>
          navigate("/owner/shop?tab=services", { replace: true }),
      }),
    guard: "owner",
  },
  {
    pattern: "/owner/settings",
    viewLoader: () => import("./views/owner/settingsView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/account",
    viewLoader: () => import("./views/owner/accountView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/billing",
    viewLoader: () => import("./views/owner/billingView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/contact",
    viewLoader: () => import("./views/owner/contactView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/analytics",
    viewLoader: () => import("./views/owner/analyticsView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/display",
    viewLoader: () => import("./views/owner/ownerDisplayView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/scan",
    viewLoader: () => import("./views/owner/scanView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/shop",
    viewLoader: () => import("./views/owner/shopView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/history",
    viewLoader: () => import("./views/owner/historyView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/customers/:id",
    viewLoader: () => import("./views/owner/customerDetailsView.js"),
    guard: "owner",
  },
  {
    pattern: "/owner/customers",
    viewLoader: () => import("./views/owner/customersView.js"),
    guard: "owner",
  },
  // Legacy direct routes — redirect to /owner/shop with the relevant tab
  {
    pattern: "/owner/rewards",
    viewLoader: () =>
      Promise.resolve({
        mount: (_, { navigate }) =>
          navigate("/owner/shop?tab=rewards", { replace: true }),
      }),
    guard: "owner",
  },
  {
    pattern: "/owner/services",
    viewLoader: () =>
      Promise.resolve({
        mount: (_, { navigate }) =>
          navigate("/owner/shop?tab=services", { replace: true }),
      }),
    guard: "owner",
  },
  {
    pattern: "/owner",
    viewLoader: () => import("./views/owner/ownerHomeView.js"),
    guard: "owner",
  },
  // Customer
  {
    pattern: "/dashboard",
    viewLoader: () => import("./views/customer/dashboardView.js"),
    guard: "customer",
  },
  {
    pattern: "/history",
    viewLoader: () => import("./views/customer/historyView.js"),
    guard: "customer",
  },
  {
    pattern: "/shop-info",
    viewLoader: () => import("./views/customer/shopInfoView.js"),
    guard: "customer",
  },
  {
    pattern: "/account",
    viewLoader: () => import("./views/customer/accountView.js"),
    guard: "customer",
  },
  {
    pattern: "/messages",
    viewLoader: () => import("./views/customer/messagesView.js"),
    guard: "customer",
  },
  // Admin impersonation — signs in via custom token, no auth guard
  {
    pattern: "/impersonate",
    viewLoader: () => import("./views/impersonateView.js"),
    guard: "any",
  },
  // Public
  {
    pattern: "/login",
    viewLoader: () => import("./views/loginView.js"),
    guard: "public",
  },
  {
    pattern: "/register",
    viewLoader: () => import("./views/registerView.js"),
    guard: "public",
  },
  {
    pattern: "/",
    viewLoader: () => {
      window.location.href = "/marketing/";
      return new Promise(() => {}); // Never resolves as we are redirecting
    },
    guard: "public",
  },
];

// ─── Internal state ───────────────────────────────────────────────────────────

/** Cleanup function returned by the currently mounted view. */
let currentUnmount = null;

/** Called by app.js on each navigation to manage the nav component. */
let onNavigateCallback = null;

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Called by app.js to wire up the nav component update logic.
 * @param {(route: object, path: string) => void} fn
 */
export function setNavigateCallback(fn) {
  onNavigateCallback = fn;
}

/**
 * Navigate to a new path using the History API.
 * @param {string} path       e.g. '/dashboard'
 * @param {{ replace?: boolean }} options
 */
export function navigate(path, { replace = false } = {}) {
  if (replace) {
    history.replaceState(null, "", path);
  } else {
    history.pushState(null, "", path);
  }
  handleRoute();
}

/**
 * Start the router. Call once after Firebase Auth resolves.
 * Handles the current URL and listens for browser back/forward navigation.
 */
export function startRouter() {
  window.addEventListener("popstate", handleRoute);
  handleRoute();
}

// ─── Route matching ───────────────────────────────────────────────────────────

/**
 * Match a URL pattern (with :param segments) against an actual path.
 * @returns {object|null} params map or null if no match
 */
function matchPattern(pattern, path) {
  const pp = pattern.split("/");
  const ap = path.split("/");
  if (pp.length !== ap.length) return null;

  const params = {};
  for (let i = 0; i < pp.length; i++) {
    if (pp[i].startsWith(":")) {
      params[pp[i].slice(1)] = decodeURIComponent(ap[i] || "");
    } else if (pp[i] !== ap[i]) {
      return null;
    }
  }
  return params;
}

function matchRoute(path) {
  // Strip trailing slash (except root)
  const cleanPath = path.length > 1 ? path.replace(/\/$/, "") : path;

  for (const route of ROUTES) {
    const params = matchPattern(route.pattern, cleanPath);
    if (params !== null) return { route, params };
  }
  return null;
}

function getEffectivePath(pathname) {
  const subdomainSlug = getActiveSubdomainSlug();

  if (pathname === "/") {
    if (subdomainSlug) {
      return `/s/${encodeURIComponent(subdomainSlug)}`;
    }
    return pathname;
  }

  // On a shop subdomain, /s/:slug paths must match the current subdomain's shop.
  // If the slug in the URL belongs to a different shop, hard-redirect to that
  // shop's own subdomain so we never render one shop's content on another's domain.
  if (subdomainSlug && pathname.startsWith("/s/")) {
    const pathSlug = decodeURIComponent(pathname.slice(3))
      .split("/")[0]
      .toLowerCase();
    if (
      pathSlug &&
      pathSlug !== "__preview__" &&
      pathSlug !== subdomainSlug.toLowerCase()
    ) {
      window.location.replace(`https://${shopCustomDomain(pathSlug)}`);
      // Return a path that will never match any route so the router idles
      return "/__redirecting__";
    }
  }

  return pathname;
}

// ─── Auth guard ───────────────────────────────────────────────────────────────

/**
 * Returns true if the route is accessible; otherwise navigates away and returns false.
 */
function checkGuard(route) {
  const { user, profile } = appState;

  if (route.guard === "public") {
    // Redirect logged-in users to their returnTo URL, or their home page
    if (user && profile) {
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get("returnTo");
      if (returnTo && returnTo.startsWith("/")) {
        navigate(returnTo, { replace: true });
      } else {
        navigate(profile.role === "owner" ? "/owner" : "/dashboard", {
          replace: true,
        });
      }
      return false;
    }
    return true;
  }

  // "any" guard — accessible to all visitors regardless of auth state
  if (route.guard === "any") return true;

  // Owner guard — also ensures onboarding is complete
  if (route.guard === "owner") {
    // If the owner somehow reaches an owner route while in preview mode,
    // clear the preview flag so they are fully back in the owner context.
    if (appState.ownerPreview) {
      appState.ownerPreview = false;
      sessionStorage.removeItem("ownerPreview");
    }
    if (!user || !profile) {
      const returnUrl = encodeURIComponent(
        window.location.pathname + window.location.search,
      );
      navigate(`/login?returnTo=${returnUrl}`, { replace: true });
      return false;
    }
    if (profile.role !== "owner") {
      navigate("/dashboard", { replace: true });
      return false;
    }
    // If onboarding not done, send to setup wizard
    // Exception: ?preview=1 is set when the setup page loads the app in an iframe
    const shop = appState.shop;
    const inPreviewMode =
      new URLSearchParams(window.location.search).get("preview") === "1";
    if (!inPreviewMode && (!shop || !shop.onboardingComplete)) {
      window.location.href = "/marketing/setup.html";
      return false;
    }
    return true;
  }

  // "customer" guard
  if (!user || !profile) {
    const returnUrl = encodeURIComponent(
      window.location.pathname + window.location.search,
    );
    navigate(`/login?returnTo=${returnUrl}`, { replace: true });
    return false;
  }

  if (route.guard === "customer" && profile.role !== "customer") {
    // Allow an owner through if they explicitly entered owner-preview mode
    const urlParams = new URLSearchParams(window.location.search);
    if (
      profile.role === "owner" &&
      (urlParams.get("ownerPreview") === "1" || appState.ownerPreview)
    ) {
      return true;
    }
    navigate("/owner", { replace: true });
    return false;
  }

  return true;
}

// ─── Route handler ────────────────────────────────────────────────────────────

async function handleRoute() {
  const path = getEffectivePath(window.location.pathname);
  const matched = matchRoute(path);

  if (!matched) {
    // Unknown path — go to role-appropriate home
    const fallback =
      appState.profile?.role === "owner"
        ? "/owner"
        : appState.user
          ? "/dashboard"
          : "/";
    navigate(fallback, { replace: true });
    return;
  }

  const { route, params } = matched;

  if (!checkGuard(route)) return;

  // Unmount previous view cleanly
  if (currentUnmount) {
    try {
      currentUnmount();
    } catch (_) {
      /* ignore cleanup errors */
    }
    currentUnmount = null;
  }

  // Update nav via app.js callback
  if (onNavigateCallback) onNavigateCallback(route, path);

  // Mount new view
  const viewEl = document.getElementById("app-view");
  if (!viewEl) return;
  viewEl.innerHTML = "";

  try {
    const mod = await route.viewLoader();
    const unmount = await mod.mount(viewEl, {
      params,
      query: new URLSearchParams(window.location.search),
      navigate,
    });
    currentUnmount = typeof unmount === "function" ? unmount : null;
  } catch (err) {
    console.error("[router] Failed to mount view at", path, err);

    // Stale chunk after a deploy — the dynamic import 404'd because the
    // filename hash changed. Auto-reload once so the browser picks up the
    // new index.html and its fresh chunk references.
    const isChunkError =
      err instanceof TypeError ||
      (err?.message && /Failed to fetch|import|load|chunk/i.test(err.message));
    if (isChunkError && !sessionStorage.getItem("_chunkReload")) {
      sessionStorage.setItem("_chunkReload", "1");
      window.location.reload();
      return;
    }
    sessionStorage.removeItem("_chunkReload");

    viewEl.innerHTML = `
      <div style="padding: var(--space-xl) var(--space-md)">
        <p class="msg msg-error">Σφάλμα φόρτωσης σελίδας. Παρακαλώ ανανεώστε.</p>
      </div>`;
  }
}
